import React, { useState } from "react";

import { buildApiUrl } from "../utils/urlHelpers";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Fax as FaxIcon,
  Emergency as EmergencyIcon,
  Accessible as AccessibleIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const PageHeader = styled(Box)(({ theme }) => {
  // Get the theme URL dynamically
  const themeUrl = window.wpData?.themeUrl || '';
  
  return {
    backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${themeUrl}/build/grey-building.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
    padding: theme.spacing(10, 0, 6, 0),
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(2),
  };
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
}));



const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.75rem",
  marginBottom: theme.spacing(3),
  color: "#0e3158",
  position: "relative",
  paddingBottom: theme.spacing(1),
  textAlign: "center",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60px",
    height: "3px",
    backgroundColor: "#f0b94d",
  },
}));

const ContactCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
  },
}));

const ContactCardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: "#0e3158",
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  "& svg": {
    marginRight: theme.spacing(1),
    color: "#f0b94d",
  },
}));

const ContactForm = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "#f9f9f9",
  borderRadius: "8px",
  marginTop: theme.spacing(4),
}));

const FormButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#f0b94d",
  color: "#0e3158",
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  marginTop: theme.spacing(2),
  "&:hover": {
    backgroundColor: "#d9a843",
  },
}));

const ContactLink = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  "& svg": {
    marginRight: theme.spacing(1),
    color: "#f0b94d",
  },
}));

const ContactInfoSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const MapPlaceholder = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "400px",
  backgroundColor: "#f5f7fa",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  "& iframe": {
    width: "100%",
    height: "100%",
    border: 0,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  color: '#0e3158',
}));

function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    consent: false,
  });

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === "consent" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate consent checkbox
    if (!formData.consent) {
      alert("Please provide your consent to collect your personal details.");
      return;
    }
    
    // Create the request body
    const requestBody = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message
    };
    
    try {
      // Send data to the WordPress REST API endpoint
      const apiUrl = buildApiUrl('contact-form/v1/submit');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert("Thank you for your message. We will contact you shortly.");
        
        // Reset form after successful submission
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          consent: false,
        });
      } else {
        // Handle API errors
        const errorMessage = data.message || "There was an error sending your message. Please try again later.";
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      alert("There was an error sending your message. Please check your internet connection and try again.");
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <SeoMetaTags 
        slug="contact-us" 
        defaultTitle="Contact Us - Sentry Law | Calgary Law Firm"
        defaultDescription="Contact Sentry Law for legal assistance in Calgary. Call 403-971-7036 or visit our office at Suite #603, 100-4th Avenue SW, Calgary AB. Free consultation available."
      />

      {/* Header Section */}
      <PageHeader>
        <Container maxWidth="lg">
          <PageTitle variant="h1">
            Contact Us
          </PageTitle>
        </Container>
      </PageHeader>

      <Container maxWidth="lg" sx={{ mb: 8 }}>


        <Grid container spacing={4} sx={{ mb: 5 }}>
          {/* Office Address */}
          <Grid item xs={12} md={6} lg={3}>
            <ContactCard>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <IconWrapper sx={{ mx: 'auto' }}>
                  <LocationIcon fontSize="large" />
                </IconWrapper>
                <ContactCardTitle variant="h6" sx={{ justifyContent: 'center' }}>
                  Office Address
                </ContactCardTitle>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Asia Pacific Centre<br />
                  Suite #603 100-4th Avenue SW<br />
                  Calgary AB, T2P 3E8
                </Typography>
              </CardContent>
            </ContactCard>
          </Grid>

          {/* Phone */}
          <Grid item xs={12} md={6} lg={3}>
            <ContactCard>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <IconWrapper sx={{ mx: 'auto' }}>
                  <PhoneIcon fontSize="large" />
                </IconWrapper>
                <ContactCardTitle variant="h6" sx={{ justifyContent: 'center' }}>
                  Phone
                </ContactCardTitle>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Phone: 403-971-7036<br />
                  Fax: 647-352-2230
                </Typography>
              </CardContent>
            </ContactCard>
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6} lg={3}>
            <ContactCard>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <IconWrapper sx={{ mx: 'auto' }}>
                  <EmailIcon fontSize="large" />
                </IconWrapper>
                <ContactCardTitle variant="h6" sx={{ justifyContent: 'center' }}>
                  Email
                </ContactCardTitle>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  David.ike@sentrylaw.ca
                </Typography>
              </CardContent>
            </ContactCard>
          </Grid>

          {/* Office Hours */}
          <Grid item xs={12} md={6} lg={3}>
            <ContactCard>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <IconWrapper sx={{ mx: 'auto' }}>
                  <TimeIcon fontSize="large" />
                </IconWrapper>
                <ContactCardTitle variant="h6" sx={{ justifyContent: 'center' }}>
                  Office Hours
                </ContactCardTitle>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Monday to Friday: 9:00 AM – 5:00 PM<br />
                  Saturday & Sunday: Closed<br />
                  Holidays: Closed
                </Typography>
              </CardContent>
            </ContactCard>
          </Grid>
        </Grid>

        {/* Map */}
        <Paper elevation={0} sx={{ p: 3, mb: 6, borderRadius: '8px' }}>
          <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, color: "#0e3158", textAlign: 'center' }}>
            Our Location
          </Typography>
          <MapPlaceholder>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d20055.323416224096!2d-114.08375847119139!3d51.04879127300353!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x53716ffb869fdcbd%3A0xb89f9543c355689f!2sDowntown%2C%20Calgary%2C%20AB!5e0!3m2!1sen!2sca!4v1712525795499!5m2!1sen!2sca" 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Sentry Law Office Location"
              aria-label="Google Maps showing Sentry Law Office Location"
            ></iframe>
          </MapPlaceholder>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AccessibleIcon sx={{ color: "#f0b94d", mr: 1 }} />
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Our office is wheelchair accessible with elevators and ramps available.
            </Typography>
          </Box>
        </Paper>

        {/* Contact Form Section */}
        <SectionTitle variant="h2" gutterBottom>
          Send Us a Message
        </SectionTitle>
        
        <Typography variant="body1" paragraph sx={{ textAlign: 'center', mb: 4 }}>
          Use the form below to send us a message. We'll get back to you as soon as possible.
        </Typography>

        <Paper elevation={0} sx={{ p: 4, backgroundColor: '#f5f7fa', borderRadius: '8px', mb: 6 }}>
          <ContactForm component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Your Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Your Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Your Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  multiline
                  rows={5}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                      color="primary"
                    />
                  }
                  label="I consent that Sentry Law may collect my personal details for contact purposes only."
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <FormButton type="submit" variant="contained" size="large">
                  Submit Message
                </FormButton>
              </Grid>
            </Grid>
          </ContactForm>
        </Paper>

        {/* Note */}
        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', borderLeft: '4px solid #f0b94d', borderRadius: '4px', mb: 4 }}>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            <strong>Privacy Note:</strong> Any information you submit through this form is kept confidential and used only to respond to your inquiry. We respect your privacy and adhere to all applicable privacy regulations.
          </Typography>
        </Paper>
      </Container>
    </>
  );
}

export default ContactUs;
